import React, { useState, useEffect, useMemo } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { connect } from 'react-redux'
import isEqual from 'lodash/isEqual'
import { Tabs, Tab, Button } from '@material-ui/core'
import { Map, List } from 'immutable'
import { css } from 'styled-components'
import { ReactComponent as PlaceIcon } from 'assets/img/place-icon-transparent.svg'
import { ReactComponent as FilterIcon } from 'assets/img/filter-icon-redesign.svg'
import {
  fetchManagementUIFilters,
  setPsfs,
  clearManagementUIStore,
  setSelectedOtherFilters,
} from 'reducers/managementUI/reducer'
import Psfs from 'lib/psfs/Psfs'
import { MANAGEMENT_UI_SCHEDULING } from 'constants/index'
import StyledButton from 'components/common/Button/Button'
import {
  FILTER_TYPE_ASSIGNED_USER,
  FILTER_TYPE_ASSIGNED_USER_GROUP,
  FILTER_TYPE_CREATED_BY,
  FILTER_TYPE_DATE,
  FILTER_TYPE_OVERDUE,
  FILTER_TYPE_RECURRENCE,
  FILTER_TYPE_STATUS,
  FILTER_TYPE_SITE,
  FILTER_TYPE_MEDIA,
  FILTER_TYPE_DUE_DATE,
  MANAGEMENT_UI_OTHER_FILTERS,
  FILTER_TYPE_ASSIGNEE,
  MANAGEMENT_UI_RECURRENCE_FILTER,
  MANAGEMENT_UI_STATUS_FILTER,
  FILTER_TYPE_DATE_RANGE_TO,
  FILTER_TYPE_DATE_RANGE_FROM,
  TODAY,
  MANAGEMENT_UI_SCHEDULING_FILTERS,
  FILTER_TYPE_DELETED,
} from 'constants/managementUI'
import { filterArrBySearchQuery } from 'helpers/common'
import { saveManagementUIFilters } from 'reducers/profile/reducer'
import withPromise from 'helpers/withPromise'
// import { createSiteTreeByLicensee } from 'helpers/siteTree'
import {
  Site,
  User,
  UserGroup,
  Profile,
  PsfsType,
  SORT_DIRECTION,
} from 'types/common'
import SearchField from 'components/common/SearchField'
import SelectInput from 'components/common/Inputs/SelectInput'
import NestedSelectInput from 'components/common/Inputs/NestedSelectInput'
import {
  SelectedOtherFilter,
  JobsManagementFilters,
} from 'types/managementUI.types'
import SelectedFiltersDropdown from 'components/common/SelectedOtherFiltersDropdown/SelectedOtherFiltersDropdown'
// eslint-disable-next-line import/no-named-as-default
import DateRangeSelector from '../common/DateRangeSelector/DateRangeSelector'
import messages from '../translate'
import OtherFilters from '../common/OtherFiltersDropdown/OtherFiltersDropdown'
import {
  FilterBarWrapper,
  FilterButton,
  StyledTabs,
  ResetFiltersButton,
  Container,
} from '../ManagementUI.styles'
import { FilterBarProps } from './FilterBar.types'

// Type definitions for site tree structure
interface SiteTreeOption {
  value: number
  label: string
}

interface SiteTreeGroup {
  id: string
  title: string
  options: SiteTreeOption[]
}

// eslint-disable-next-line max-statements
function FilterBar({
  intl: { formatMessage },
  activeTab,
  storePsfs,
  setPsfs,
  clearManagementUIStore,
  fetchManagementUIFilters,
  saveManagementUIFilters,
  filtersList,
  setIsUpdating,
  profile,
  tmpSites,
  setTmpSites,
  tmpUsers,
  setTmpUsers,
  tmpUserGroups,
  setTmpUserGroups,
  tmpCreatedBy,
  setTmpCreatedBy,
  selectedOtherFilters,
  setSelectedOtherFilters,
  tmpRecurrence,
  setTmpRecurrence,
  tmpStatus,
  setTmpStatus,
  featureFlags,
  toggleIsSidebarOpened,
  fetchManagementUITable,
  setSearchValue,
  searchValue,
  page,
  rowsPerPage,
  setPage,
  sortColumn,
  sortDirection,
}: FilterBarProps & WrappedComponentProps) {
  const {
    id: currentUserId,
    jobsManagementFilters,
    canSeeDeletedJobs,
  } = profile.toJS()
  const filtersListData = filtersList.toJS()
  const selectedOtherFiltersData = selectedOtherFilters.toJS()
  const {
    [FILTER_TYPE_DATE]: appliedDate,
    [FILTER_TYPE_DATE_RANGE_FROM]: appliedDateRangeFrom,
    [FILTER_TYPE_DATE_RANGE_TO]: appliedDateRangeTo,
    [FILTER_TYPE_OVERDUE]: appliedOverdue,
    [FILTER_TYPE_DELETED]: appliedDeleted,
    [FILTER_TYPE_SITE]: appliedSites,
    [FILTER_TYPE_ASSIGNED_USER]: appliedUsers,
    [FILTER_TYPE_ASSIGNED_USER_GROUP]: appliedUserGroups,
    [FILTER_TYPE_CREATED_BY]: appliedCreatedBy,
    [FILTER_TYPE_RECURRENCE]: appliedRecurrence,
    [FILTER_TYPE_STATUS]: appliedStatus,
    [FILTER_TYPE_MEDIA]: appliedMedia,
    [FILTER_TYPE_DUE_DATE]: appliedDueDate,
  } = jobsManagementFilters

  const {
    [FILTER_TYPE_SITE]: sitesListFilter,
    [FILTER_TYPE_ASSIGNED_USER]: usersListFilter,
    [FILTER_TYPE_ASSIGNED_USER_GROUP]: userGroupsListFilter,
    [FILTER_TYPE_CREATED_BY]: createdByListFilter,
  } = filtersListData
  const isSchedulingTab = useMemo(
    () =>
      activeTab === MANAGEMENT_UI_SCHEDULING && featureFlags.JOB_SCHEDULING_V2,
    [activeTab, featureFlags.JOB_SCHEDULING_V2],
  )

  const isSiteTreeEnabled = featureFlags.MENAGMENT_CONSOLE_SITE_TREE_VIEW

  const siteTreeData: SiteTreeGroup[] = useMemo(() => {
    if (!isSiteTreeEnabled || !sitesListFilter?.length) return []

    const sitesWithGroups = sitesListFilter.filter(
      (item: Site & { children?: Site[] }) => item?.children?.length,
    )
    const sitesWithoutGroups = sitesListFilter.filter(
      (item: Site & { children?: Site[] }) => !item?.children?.length,
    )

    const groupedBySiteGroups = sitesWithGroups.reduce(
      (
        acc: Record<string, SiteTreeGroup>,
        item: Site & { children?: Site[] },
      ) => {
        if (item?.children?.length) {
          const groupId = item.id
          const { name } = item
          const groupKey = `group_${groupId}`

          if (!acc[groupKey]) {
            acc[groupKey] = {
              id: groupKey,
              title: name,
              options: [],
            }
          }

          acc[groupKey].options = item?.children.map((site: Site) => ({
            value: site.id,
            label: site.name,
          }))
        }

        return acc
      },
      {},
    )

    const groups: SiteTreeGroup[] = Object.values(groupedBySiteGroups)
    if (sitesWithoutGroups.length > 0) {
      groups.push({
        id: 'other-sites',
        title: 'Other Sites',
        options: sitesWithoutGroups.map((site: Site) => ({
          value: site.id,
          label: site.name,
        })),
      })
    }

    if (groups.length === 0) {
      return [
        {
          id: 'all-sites',
          title: 'All Sites',
          options: sitesListFilter.map((site: Site) => ({
            value: site.id,
            label: site.name,
          })),
        },
      ]
    }

    return groups
  }, [isSiteTreeEnabled, sitesListFilter])

  const selectedFiltersForTab = useMemo(
    () =>
      selectedOtherFiltersData.filter(
        ({ value }: { value: string }) =>
          !isSchedulingTab || MANAGEMENT_UI_SCHEDULING_FILTERS.includes(value),
      ),
    [JSON.stringify(selectedOtherFiltersData), isSchedulingTab],
  )

  const [
    otherFiltersMenuAnchor,
    setOtherFiltersMenuAnchor,
  ] = useState<HTMLElement | null>(null)
  const [assigneesSearchQuery, setAssigneesSearchQuery] = useState('')
  const [createdBySearchQuery, setCreatedBySearchQuery] = useState('')
  const [filteredUsersListFilter, setFilteredUsersListFilter] = useState(
    usersListFilter,
  )
  const [
    filteredUserGroupsListFilter,
    setFilteredUserGroupsListFilter,
  ] = useState(userGroupsListFilter)
  const [
    filteredCreatedByListFilter,
    setFilteredCreatedByListFilter,
  ] = useState(createdByListFilter)

  const hasSelectedOtherFilters = !!selectedFiltersForTab.length
  const isDeletedTabAvailable =
    featureFlags?.SEE_AND_RESTORE_DELETED_JOBS && canSeeDeletedJobs

  const setFilter = (storeName: string, value: unknown, psfs: PsfsType) => {
    if (value) {
      psfs.addFilter(storeName, value, 'string')
    } else {
      psfs.clearFilter(storeName)
    }
  }

  const updatePsfsQuery = (
    storeName: string,
    value: string,
    psfs: PsfsType,
  ) => {
    switch (storeName) {
      case FILTER_TYPE_DATE: {
        setFilter(storeName, value, psfs)

        break
      }
      case FILTER_TYPE_DATE_RANGE_FROM:
      case FILTER_TYPE_DATE_RANGE_TO: {
        setFilter(storeName, value, psfs)

        break
      }
      case FILTER_TYPE_OVERDUE:
      case FILTER_TYPE_DELETED:
      case FILTER_TYPE_DUE_DATE: {
        setFilter(storeName, value, psfs)

        break
      }
      case FILTER_TYPE_SITE:
      case FILTER_TYPE_ASSIGNED_USER:
      case FILTER_TYPE_ASSIGNED_USER_GROUP:
      case FILTER_TYPE_STATUS:
      case FILTER_TYPE_RECURRENCE: {
        if (value.length) {
          psfs.addFilter(storeName, value)
        } else {
          psfs.clearFilter(storeName)
        }

        break
      }
      case FILTER_TYPE_MEDIA: {
        if (value !== null) {
          psfs.addFilter(storeName, value ? '1' : '0', 'string')
        } else {
          psfs.clearFilter(storeName)
        }

        break
      }
      default:
    }
  }

  const clearPagination = () => {
    /* eslint no-underscore-dangle: 0 */
    storePsfs._pagination?.clearPagination()
    setPage(0)
  }

  const fetchDataByFilter = () => {
    const { path } = storePsfs
    const psfs = new Psfs(path)

    Object.keys(jobsManagementFilters).forEach(storeName => {
      updatePsfsQuery(storeName, jobsManagementFilters[storeName], psfs)
    })

    if (
      !jobsManagementFilters[FILTER_TYPE_DATE] &&
      !jobsManagementFilters[FILTER_TYPE_DATE_RANGE_FROM] &&
      !jobsManagementFilters[FILTER_TYPE_DATE_RANGE_TO]
    ) {
      psfs.addFilter(FILTER_TYPE_DATE, TODAY, 'string')
    }

    if (!jobsManagementFilters[FILTER_TYPE_CREATED_BY].length) {
      psfs.clearFilter(FILTER_TYPE_CREATED_BY)
    } else if (jobsManagementFilters[FILTER_TYPE_CREATED_BY].length) {
      psfs.addFilter(
        FILTER_TYPE_CREATED_BY,
        jobsManagementFilters[FILTER_TYPE_CREATED_BY],
      )
    } else {
      psfs.addFilter(FILTER_TYPE_CREATED_BY, [currentUserId])
    }

    if (sortColumn) {
      psfs.addSort(
        sortColumn,
        sortDirection ? SORT_DIRECTION.ASC : SORT_DIRECTION.DESC,
      )
    }

    psfs.addPagination(page + 1, rowsPerPage)
    setPsfs(psfs)
  }

  useEffect(() => {
    fetchManagementUIFilters()

    return () => {
      clearManagementUIStore()
    }
  }, [])

  useEffect(() => {
    setFilteredUsersListFilter(usersListFilter)
  }, [usersListFilter])

  useEffect(() => {
    setFilteredUserGroupsListFilter(userGroupsListFilter)
  }, [userGroupsListFilter])

  useEffect(() => {
    setFilteredCreatedByListFilter(createdByListFilter)
  }, [createdByListFilter])

  useEffect(() => {
    const filteredUsers = filterArrBySearchQuery(
      usersListFilter,
      assigneesSearchQuery,
    )
    const filteredUserGroups = filterArrBySearchQuery(
      userGroupsListFilter,
      assigneesSearchQuery,
    )
    const filteredCreatedBy = filterArrBySearchQuery(
      createdByListFilter,
      assigneesSearchQuery,
    )

    setFilteredUsersListFilter(filteredUsers)
    setFilteredUserGroupsListFilter(filteredUserGroups)
    setFilteredCreatedByListFilter(filteredCreatedBy)
  }, [assigneesSearchQuery])

  useEffect(() => {
    const filteredCreatedBy = filterArrBySearchQuery(
      createdByListFilter,
      createdBySearchQuery,
    )

    setFilteredCreatedByListFilter(filteredCreatedBy)
  }, [createdBySearchQuery])

  useEffect(() => {
    fetchManagementUITable()
  }, [storePsfs, activeTab, searchValue])

  useEffect(() => {
    fetchDataByFilter()
  }, [
    appliedOverdue,
    appliedDeleted,
    appliedMedia,
    appliedDate,
    appliedDateRangeFrom,
    appliedDateRangeTo,
    appliedDueDate,
    JSON.stringify(appliedSites),
    JSON.stringify(appliedUsers),
    JSON.stringify(appliedUserGroups),
    JSON.stringify(appliedCreatedBy),
    JSON.stringify(appliedRecurrence),
    JSON.stringify(appliedStatus),
    page,
    rowsPerPage,
    sortColumn,
    sortDirection,
  ])

  // eslint-disable-next-line max-statements
  useEffect(() => {
    const filteredAppliedCreatedBy = usersListFilter.filter(({ id }: User) =>
      appliedCreatedBy.find((user: number) => user === id),
    )
    const filteredAppliedUsers = usersListFilter.filter(({ id }: User) =>
      appliedUsers.find((user: number) => user === id),
    )
    const filteredAppliedUserGroups = userGroupsListFilter.filter(
      ({ id }: UserGroup) =>
        appliedUserGroups.find((group: number) => group === id),
    )
    const filteredAppliedRecurrence = MANAGEMENT_UI_RECURRENCE_FILTER.filter(
      ({ value }) =>
        appliedRecurrence.find((recurrence: string) => recurrence === value),
    )
    const filteredAppliedStatus = MANAGEMENT_UI_STATUS_FILTER.filter(
      ({ value }) => appliedStatus.find((status: string) => status === value),
    )
    const filters = []

    if (filteredAppliedCreatedBy.length) {
      const filter = MANAGEMENT_UI_OTHER_FILTERS.find(
        ({ value }) => value === FILTER_TYPE_CREATED_BY,
      )
      const isSelected = selectedFiltersForTab.find(
        ({ value }: SelectedOtherFilter) => value === FILTER_TYPE_CREATED_BY,
      )

      if (!isSelected) {
        filters.push(filter)
      }
    }

    if (filteredAppliedUsers.length || filteredAppliedUserGroups.length) {
      const filter = MANAGEMENT_UI_OTHER_FILTERS.find(
        ({ value }) => value === FILTER_TYPE_ASSIGNEE,
      )
      const isSelected = selectedFiltersForTab.find(
        ({ value }: SelectedOtherFilter) => value === FILTER_TYPE_ASSIGNEE,
      )

      if (!isSelected) {
        filters.push(filter)
      }
    }

    if (filteredAppliedStatus.length) {
      const filter = MANAGEMENT_UI_OTHER_FILTERS.find(
        ({ value }) => value === FILTER_TYPE_STATUS,
      )
      const isSelected = selectedFiltersForTab.find(
        ({ value }: SelectedOtherFilter) => value === FILTER_TYPE_STATUS,
      )

      if (!isSelected) {
        filters.push(filter)
      }
    }

    if (filteredAppliedRecurrence.length) {
      const filter = MANAGEMENT_UI_OTHER_FILTERS.find(
        ({ value }) => value === FILTER_TYPE_RECURRENCE,
      )
      const isSelected = selectedFiltersForTab.find(
        ({ value }: SelectedOtherFilter) => value === FILTER_TYPE_RECURRENCE,
      )

      if (!isSelected) {
        filters.push(filter)
      }
    }

    if (typeof appliedMedia === 'boolean') {
      const filter = MANAGEMENT_UI_OTHER_FILTERS.find(
        ({ value }) => value === FILTER_TYPE_MEDIA,
      )
      const isSelected = selectedFiltersForTab.find(
        ({ value }: SelectedOtherFilter) => value === FILTER_TYPE_MEDIA,
      )

      if (!isSelected) {
        filters.push(filter)
      }
    }

    if (appliedDueDate) {
      const filter = MANAGEMENT_UI_OTHER_FILTERS.find(
        ({ value }) => value === FILTER_TYPE_DUE_DATE,
      )
      const isSelected = selectedFiltersForTab.find(
        ({ value }: SelectedOtherFilter) => value === FILTER_TYPE_DUE_DATE,
      )

      if (!isSelected) {
        filters.push(filter)
      }
    }

    if (filters.length) {
      setSelectedOtherFilters([...selectedFiltersForTab, ...filters])
    }

    setTmpStatus(appliedStatus)
    setTmpRecurrence(appliedRecurrence)
    setTmpCreatedBy(filteredAppliedCreatedBy)
    setTmpUsers(filteredAppliedUsers)
    setTmpUserGroups(filteredAppliedUserGroups)
  }, [
    appliedMedia,
    appliedDueDate,
    JSON.stringify(usersListFilter),
    JSON.stringify(userGroupsListFilter),
    JSON.stringify(appliedUserGroups),
    JSON.stringify(appliedCreatedBy),
    JSON.stringify(appliedUsers),
    JSON.stringify(appliedRecurrence),
    JSON.stringify(appliedStatus),
  ])

  const applyFilter = (
    jobs_management_filters: Partial<JobsManagementFilters>,
  ) => {
    const saveFiltersPayload = {
      jobs_management_filters,
    }
    clearPagination()
    setIsUpdating(true)
    withPromise(saveManagementUIFilters, saveFiltersPayload).then(() => {
      setIsUpdating(false)
    })
  }

  useEffect(() => {
    if (sitesListFilter.length) {
      // For tree select, we need to handle the filtering differently
      if (isSiteTreeEnabled) {
        // When using tree select, we need to check if the applied sites are still valid
        // by checking if they exist in the current site tree data
        const allAvailableSiteIds = siteTreeData.flatMap(group =>
          group.options.map(option => option.value),
        )

        const filteredSites = (Array.isArray(appliedSites)
          ? appliedSites
          : [appliedSites]
        ).filter(appliedSite => allAvailableSiteIds.includes(appliedSite))

        // Only apply filter if the filtered sites are different from applied sites
        if (!isEqual(filteredSites, appliedSites)) {
          applyFilter({
            [FILTER_TYPE_SITE]: filteredSites,
          })
        }

        setTmpSites(filteredSites)
      } else {
        // Original logic for regular select
        const filteredSites = (Array.isArray(appliedSites)
          ? appliedSites
          : [appliedSites]
        ).filter(appliedSite =>
          sitesListFilter.some(({ id }: { id: number }) => id === appliedSite),
        )

        if (!isEqual(filteredSites, appliedSites)) {
          applyFilter({
            [FILTER_TYPE_SITE]: filteredSites,
          })
        }

        setTmpSites(filteredSites)
      }
    }
  }, [
    JSON.stringify(sitesListFilter),
    JSON.stringify(appliedSites),
    isSiteTreeEnabled,
    siteTreeData,
  ])

  const clearSelectedOtherFilters = () => {
    const saveFiltersPayload = {
      jobs_management_filters: {
        [FILTER_TYPE_CREATED_BY]: [],
        [FILTER_TYPE_ASSIGNED_USER]: [],
        [FILTER_TYPE_ASSIGNED_USER_GROUP]: [],
        [FILTER_TYPE_RECURRENCE]: [],
        ...(!isSchedulingTab
          ? {
              [FILTER_TYPE_STATUS]: [],
              [FILTER_TYPE_MEDIA]: null,
              [FILTER_TYPE_DUE_DATE]: null,
            }
          : {}),
      },
    }

    setIsUpdating(true)
    setSelectedOtherFilters(
      selectedOtherFiltersData.filter(
        ({ value }: { value: string }) =>
          isSchedulingTab && !MANAGEMENT_UI_SCHEDULING_FILTERS.includes(value),
      ),
    )

    withPromise(saveManagementUIFilters, saveFiltersPayload).then(() => {
      setIsUpdating(false)
    })
  }

  const onResetOtherFilters = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
  ) => {
    if (!isSchedulingTab) {
      setTmpStatus([])
    }

    setTmpUsers([])
    setTmpUserGroups([])
    setTmpCreatedBy([])
    setTmpRecurrence([])
    setAssigneesSearchQuery('')
    setCreatedBySearchQuery('')
    clearSelectedOtherFilters()
    event.stopPropagation()
  }

  const isSingleFilterSelected = useMemo(
    () => selectedOtherFiltersData.length === 1,
    [selectedOtherFiltersData.length],
  )

  let selectedTab = null

  if (jobsManagementFilters[FILTER_TYPE_OVERDUE] === true) {
    selectedTab = FILTER_TYPE_OVERDUE
  } else if (
    isDeletedTabAvailable &&
    jobsManagementFilters[FILTER_TYPE_DELETED] === true
  ) {
    selectedTab = FILTER_TYPE_DELETED
  } else if (
    jobsManagementFilters[FILTER_TYPE_OVERDUE] === null &&
    jobsManagementFilters[FILTER_TYPE_DELETED] === null
  ) {
    selectedTab = null // Both are null, All should be selected
  }

  return (
    <FilterBarWrapper wrap="wrap">
      <div>
        {isSiteTreeEnabled ? (
          <NestedSelectInput
            options={siteTreeData}
            value={Array.isArray(tmpSites) ? tmpSites : [tmpSites]}
            onChange={(values: (string | number)[]) => {
              const normalizeValue = (value: string | number): string =>
                String(value)

              const isGroupId = (value: string | number): boolean =>
                siteTreeData.some(
                  group => normalizeValue(group.id) === normalizeValue(value),
                )

              const normalizedCurrentSites = Array.isArray(tmpSites)
                ? tmpSites.map(normalizeValue)
                : [normalizeValue(tmpSites)]
              const selectedGroups = values.filter(value => isGroupId(value))

              if (selectedGroups.length > 0) {
                let finalSelectedSites: string[] = [...normalizedCurrentSites]

                selectedGroups.forEach(groupId => {
                  const group = siteTreeData.find(
                    g => normalizeValue(g.id) === normalizeValue(groupId),
                  )

                  if (group) {
                    const groupSites = group.options.map(option =>
                      normalizeValue(option.value),
                    )
                    const allGroupSitesSelected = groupSites.every(siteId =>
                      normalizedCurrentSites.includes(siteId),
                    )

                    if (allGroupSitesSelected) {
                      finalSelectedSites = finalSelectedSites.filter(
                        siteId => !groupSites.includes(siteId),
                      )
                    } else {
                      groupSites.forEach(siteId => {
                        if (!finalSelectedSites.includes(siteId)) {
                          finalSelectedSites.push(siteId)
                        }
                      })
                    }
                  }
                })
                const valuesToSet =
                  finalSelectedSites.map(value => Number(value)) || []
                setTmpSites(valuesToSet)
              } else {
                const valuesToSet = values.map(value => Number(value)) || []
                setTmpSites(valuesToSet)
              }
            }}
            multiple
            label={formatMessage(messages.allSites)}
            hasSearchField
            hasSelectAllOption
            groupSelectable
            startAdornment={PlaceIcon}
            onClose={() => {
              if (!isEqual(tmpSites, appliedSites)) {
                applyFilter({
                  [FILTER_TYPE_SITE]: tmpSites,
                })
              }
            }}
          />
        ) : (
          <SelectInput
            startAdornment={PlaceIcon}
            hasSearchField
            hasSelectAllOption
            multiple
            label={formatMessage(messages.allSites)}
            displayLabelIfAllSelected
            options={sitesListFilter.map(({ id, name }: Site) => ({
              value: id,
              label: name,
            }))}
            value={Array.isArray(tmpSites) ? tmpSites : [tmpSites]}
            onChange={setTmpSites}
            onClose={() => {
              if (!isEqual(tmpSites, appliedSites)) {
                applyFilter({
                  [FILTER_TYPE_SITE]: tmpSites,
                })
              }
            }}
          />
        )}
      </div>
      <div>
        <FilterButton
          isMenuOpen={!!otherFiltersMenuAnchor}
          isSingleFilterSelected={isSingleFilterSelected}>
          <Button
            type="button"
            onClick={({ currentTarget }) =>
              setOtherFiltersMenuAnchor(currentTarget)
            }>
            <div>
              <FilterIcon />
              <span>
                {isSingleFilterSelected
                  ? formatMessage(
                      messages[
                        selectedOtherFiltersData[0]
                          .label as keyof typeof messages
                      ],
                    )
                  : selectedOtherFiltersData.length ||
                    formatMessage(messages.filters)}
              </span>
            </div>
            {hasSelectedOtherFilters && (
              <ResetFiltersButton
                onClick={event => onResetOtherFilters(event)}
                isMenuOpen={!!otherFiltersMenuAnchor}
              />
            )}
          </Button>
        </FilterButton>
        {hasSelectedOtherFilters ? (
          <SelectedFiltersDropdown
            otherFiltersMenuAnchor={otherFiltersMenuAnchor}
            setOtherFiltersMenuAnchor={setOtherFiltersMenuAnchor}
            assigneesSearchQuery={assigneesSearchQuery}
            setAssigneesSearchQuery={setAssigneesSearchQuery}
            createdBySearchQuery={createdBySearchQuery}
            setCreatedBySearchQuery={setCreatedBySearchQuery}
            tmpCreatedBy={tmpCreatedBy}
            setTmpCreatedBy={setTmpCreatedBy}
            tmpRecurrence={tmpRecurrence}
            setTmpRecurrence={setTmpRecurrence}
            tmpStatus={tmpStatus}
            setTmpStatus={setTmpStatus}
            tmpUsers={tmpUsers}
            setTmpUsers={setTmpUsers}
            tmpUserGroups={tmpUserGroups}
            setTmpUserGroups={setTmpUserGroups}
            filteredUsersListFilter={filteredUsersListFilter}
            filteredUserGroupsListFilter={filteredUserGroupsListFilter}
            filteredCreatedByListFilter={filteredCreatedByListFilter}
            applyFilter={applyFilter}
            setIsUpdating={setIsUpdating}
            activeTab={activeTab}
            isSchedulingTab={isSchedulingTab}
            clearPagination={clearPagination}
          />
        ) : (
          <OtherFilters
            activeTab={activeTab}
            anchorEl={otherFiltersMenuAnchor}
            setAnchorEl={setOtherFiltersMenuAnchor}
            applyFilter={applyFilter}
          />
        )}
      </div>
      {!isSchedulingTab && (
        <>
          <DateRangeSelector
            applyFilter={applyFilter}
            appliedDate={appliedDate}
            appliedDateRangeFrom={appliedDateRangeFrom}
            appliedDateRangeTo={appliedDateRangeTo}
          />
          <StyledTabs>
            <Tabs
              value={selectedTab}
              onChange={(_, value) => {
                const filter: {
                  overdue: boolean | null
                  deleted: boolean | null
                } = {
                  overdue: null,
                  deleted: null,
                }

                if (value === FILTER_TYPE_OVERDUE) {
                  filter.overdue = true
                } else if (value === FILTER_TYPE_DELETED) {
                  filter.deleted = true
                }

                applyFilter(filter)
              }}>
              <Tab value={null} label={formatMessage(messages.all)} />
              <Tab
                value={FILTER_TYPE_OVERDUE}
                label={formatMessage(messages.overdue)}
              />
              {isDeletedTabAvailable && (
                <Tab
                  value={FILTER_TYPE_DELETED}
                  label={formatMessage(messages.deleted)}
                />
              )}
            </Tabs>
          </StyledTabs>
        </>
      )}
      <SearchField
        placeholder={formatMessage(messages.search)}
        value={searchValue}
        setValue={val => {
          clearPagination()
          setSearchValue(val)
        }}
      />
      {featureFlags.JOB_SCHEDULING_V2 && (
        <Container>
          <StyledButton
            onClick={toggleIsSidebarOpened}
            customStyles={css`
              margin-left: auto;
            `}>
            {formatMessage(messages.planNewJob)}
          </StyledButton>
        </Container>
      )}
    </FilterBarWrapper>
  )
}

export default connect(
  (state: {
    getIn: (
      path: Array<string>,
    ) =>
      | PsfsType
      | Map<string, User | UserGroup | Site>
      | List<SelectedOtherFilter>
      | Map<string, Profile>
      | Record<string, boolean>
  }) => ({
    storePsfs: state.getIn(['managementUI', 'storePsfs']) as PsfsType,
    filtersList: state.getIn(['managementUI', 'filtersList']) as Map<
      string,
      User | UserGroup | Site
    >,
    selectedOtherFilters: state.getIn([
      'managementUI',
      'selectedOtherFilters',
    ]) as List<SelectedOtherFilter>,
    profile: state.getIn(['profile', 'profile']) as Map<string, Profile>,
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']) as Record<
      string,
      boolean
    >,
  }),
  {
    setPsfs,
    clearManagementUIStore,
    fetchManagementUIFilters,
    saveManagementUIFilters,
    setSelectedOtherFilters,
  },
)(injectIntl(FilterBar))
